import { NavigationItem, SEPARATOR } from '../../../data/types';
import { createTag, mapTags } from '../../../utils/tag-creators';
import { SubSubMenu } from './SubSubMenu';
import * as s from './Navigation.scss';
import { DropdownArrow } from './DropdownArrow/DropdownArrow';
import { MenuItemProps } from './MainMenuItem';

export interface SubMenuItemProps {
  item: NavigationItem;
  t(key: string): string;
  language: string;
}

export const ArrowRight = () => {
  return `<span class="${s.arrowRight}">→</span>`;
};

export const SubMenuItemButton = ({ item, t, language }: MenuItemProps, id: string) => {
  if (item === SEPARATOR) {
    return ``;
  }

  if (item.href) {
    const value = `
      ${t(item.text)}
    `;

    const link = createTag('a', value, {
      class: `${s.link}`,
      target: item.target,
      href: item.href,
      role: language === 'en' ? 'menuitem' : undefined,
      'data-bi-name': item.name,
      'data-bi-row': 'second',
    });
    return `
      <div class="${s.linkWrapper}">
        ${link}
        <div class="${s.description}">${t(item.description || '')}</div>
      </div>
    `;
  }

  return `
    <div class="${s.linkWrapper}">
      <a
        id="${id}"
        class="${s.link}"
        data-bi-name="${item.name}"
        data-bi-row="second"
        data-toggle="collapse"
        aria-expanded="false"
        data-hook="wix-header-open-third-menu"
        role="button"
        href="#"
      >
        ${t(item.text)}
        ${item.subs?.length ? DropdownArrow(s.arrow) : ''}
        ${item.subs?.length ? ArrowRight() : ''}
      </a>
    </div>
  `;
};

export const SubMenuItem = ({ item, t, language }: SubMenuItemProps, index?: number) => {
  const id = `sub-menu-item-${item.name}`;
  const cssClasses = [s.subMenuItem];
  if (item.subs?.length) {
    cssClasses.push(s.hasItems);
    if (index === 0) {
      cssClasses.push(s.selected);
    }
  }

  return `
    <li class="${cssClasses.join(' ')}">
      ${SubMenuItemButton({ item, t, language }, id)}
      ${item.subs ? SubSubMenu({ items: item.subs, labelledBy: id, t, language }) : ''}
    </li>
  `;
};

export interface SubMenuProps {
  labelledBy: string;
  items: NavigationItem[];
  t(key: string): string;
  language: string;
}

export const SubMenu = ({ labelledBy, items, t, language }: SubMenuProps) => {
  if (!items.length) {
    return '';
  }

  const hasThirdLevelMenu = items.filter((item) => item.subs?.length).length;

  return `
    <div class="${s.subMenuContainer} ${hasThirdLevelMenu ? '' : s.noSecondLevelMenu}">
      <div class="${s.subMenuLeftPart}" role="region" aria-labelledby="${labelledBy}">
        <ul
          class="${s.subMenu}"
          ${language === 'en' ? 'role="menu"' : ''}
        >
          ${mapTags(items, (item, index) => SubMenuItem({ item, t, language }, index))}
        </ul>
      </div>
      <div class="${s.subMenuRightPart}"></div>
    </div>
  `;
};
